import React from "react";
import { HeartPulse, Users, Sparkles, Bell, MessageCircleHeart, Flame, Baby } from "lucide-react";

const FEATURES = [
  {
    icon: Baby, tag: "Pregnancy & Postpartum · New", color: "#E7A6C4", featured: true,
    title: "A pregnancy journey you walk together",
    body: "The moment you're expecting, WYVN transforms. Cycle tracking gently pauses and a warm, week-by-week journey begins — guided by Lumi, our dedicated care coach — with safe movement, daily support, and caring cues for your partner right through to postpartum.",
    points: ["Week-by-week milestones, baby size & due-date countdown", "Safe-to-do vs. best-to-avoid movement each trimester", "Daily notes for her AND her partner, plus full postpartum care"],
  },
  {
    icon: HeartPulse, tag: "For her · Cycle Sync", color: "#DC6A7A",
    title: "A cycle tracker that trains with your body",
    body: "Log your cycle and WYVN adapts — energy insights, phase-aware workouts, and gentle daily nudges through every phase so you never fight your own rhythm.",
    points: ["Phase-by-phase energy & training guidance", "Daily motivation tuned to how you feel", "Private by default — your data is yours"],
  },
  {
    icon: MessageCircleHeart, tag: "Partner Sync", color: "#6AA6FF",
    title: "Let the people who love you show up right",
    body: "Opt in to share with your partner. They'll get gentle, respectful cues — when to support you, when to love on you, and when to simply give you space.",
    points: ["Opt-in, revocable partner sharing", "Timely 'how to support' prompts", "Zero guesswork, more connection"],
  },
  {
    icon: Flame, tag: "For him · Daily Edge", color: "#D4924A",
    title: "The men's side keeps her in the loop too",
    body: "Daily Edge tracks his energy and mood. When connected, your partner gets a light read on how you're doing — so support flows both ways.",
    points: ["Energy & mood check-ins", "Shared status with your partner", "Build streaks, stay accountable"],
  },
  {
    icon: Users, tag: "Family Plan", color: "#7A9E7E",
    title: "One plan, the whole household moving",
    body: "Bring your family in. Everyone gets their own space, and the family circle can cheer each other on with shared activity and thoughtful notifications.",
    points: ["Private spaces for each member", "Shared wins & encouragement", "Family notifications that matter"],
  },
  {
    icon: Sparkles, tag: "AI Coaches", color: "#A9CCFF",
    title: "Five coaches, always in your corner",
    body: "Judgement-free AI coaches for training, nutrition, mindset and recovery — plus Lumi, your gentle care coach for pregnancy, postpartum and cycle. Always in your language, on your terms.",
    points: ["Personalised, always-on guidance", "Premium Elite unlocks all four + unlimited"],
  },
  {
    icon: Bell, tag: "Daily Motivation", color: "#7DB2FF",
    title: "Nudges that actually move you",
    body: "Streak reminders, milestone celebrations and daily motivation land right when they help most — so showing up becomes the easy choice.",
    points: ["Smart streak & milestone nudges", "Choose exactly what you hear about"],
  },
];

export function FeatureShowcase({ onJoin }) {
  return (
    <section id="features" className="relative mx-auto max-w-6xl px-6 py-24">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">See it before you download</p>
        <h2 className="mt-3 font-display text-4xl font-semibold text-mist sm:text-5xl">The features that make WYVN different</h2>
        <p className="mx-auto mt-4 max-w-lg text-base text-mist-muted">Pregnancy &amp; postpartum, cycle sync, partner support, family plans, AI coaching and daily motivation — the whole experience, working together.</p>
      </div>

      <div className="mt-16 grid gap-6 md:grid-cols-2">
        {FEATURES.map((f, i) => (
          <div key={f.title} className={`slate-card group relative overflow-hidden rounded-[1.75rem] p-7 ${f.featured ? "md:col-span-2 ring-1" : ""}`} style={{ animationDelay: `${i * 60}ms`, ...(f.featured ? { boxShadow: `0 0 0 1px ${f.color}33` } : {}) }}>
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full blur-[60px] opacity-40 transition-opacity group-hover:opacity-70" style={{ background: f.color }} />
            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${f.color}22`, color: f.color }}><f.icon size={22} /></span>
                <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider" style={{ color: f.color }}>{f.tag}</span>
              </div>
              <h3 className="mt-5 text-xl font-semibold text-mist">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-mist-muted">{f.body}</p>
              <ul className="mt-4 space-y-1.5">
                {f.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-mist">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: f.color }} /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 text-center">
        <button onClick={onJoin} className="btn-primary" data-testid="showcase-join-btn">Start free — 30 days of Premium Elite</button>
        <p className="mt-3 text-xs text-mist-muted">Full access during your trial. Choose a plan when it ends — or keep the free version.</p>
      </div>
    </section>
  );
}
